import { AfterViewInit, DestroyRef, Directive, ElementRef, EventEmitter, inject, Output, Renderer2 } from '@angular/core';

export type A11yDialogCloseRequestSource = 'backdrop' | 'escape';

/**
 * For an ngx-bootstrap modal that must ask before it closes (for example "Are you sure?").
 * ngx-bootstrap cannot veto a close once it fires, so open the modal with `ignoreBackdropClick: true` and
 * `keyboard: false`; this directive then reports a backdrop click or Escape as a close request instead.
 * Listeners sit on the modal's own `.modal` container, so a modal stacked on top does not trigger them.
 */
@Directive({
  selector: '[khA11yDialogCloseRequest]'
})
export class A11yDialogCloseRequestDirective implements AfterViewInit {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);

  @Output('khA11yDialogCloseRequest') public readonly closeRequest = new EventEmitter<A11yDialogCloseRequestSource>();

  public ngAfterViewInit(): void {
    const modalElement = this.elementRef.nativeElement.closest('.modal');
    if (!modalElement) {
      return;
    }

    const stopBackdropClick = this.renderer.listen(modalElement, 'click', (event: MouseEvent) => {
      if (event.target === modalElement) {
        this.closeRequest.emit('backdrop');
      }
    });
    const stopEscape = this.renderer.listen(modalElement, 'keydown.escape', () => this.closeRequest.emit('escape'));

    this.destroyRef.onDestroy(() => {
      stopBackdropClick();
      stopEscape();
    });
  }
}
